"use client";

import { Alignment, EventType, Fit, Layout, type Rive, useRive } from "@rive-app/react-canvas";
import { useEffect } from "react";

import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";
import { cn } from "@/lib/utils";

/** Default state machine name from the Rive editor. */
export const DEFAULT_RIVE_STATE_MACHINE = "State Machine 1";

interface RiveTileProps {
  src: string;
  className?: string;
  /**
   * Names the animation for screen readers. Without one the animation is decorative and
   * hidden from them, like an image with empty alt text.
   */
  ariaLabel?: string;
  /**
   * State machine to play. Defaults to `null` (Rive's legacy first-timeline autoplay)
   * so arbitrary uploaded `.riv` files in the showcase grid keep working.
   * Pass a name (e.g. {@link DEFAULT_RIVE_STATE_MACHINE}) only for known assets.
   */
  stateMachines?: string | null;
  /**
   * When false, the instance stays mounted but is paused (useful for preloaded swaps).
   * Defaults to true.
   */
  active?: boolean;
  /**
   * Play a state machine from the first frame (so click and hover listeners run) and
   * auto-bind the file's default view model. The state machine is passed at init;
   * waiting until after load leaves the canvas blank. Falls back to timeline playback
   * when the file has no state machine. Ignored when `stateMachines` is given.
   */
  interactive?: boolean;
}

function isFileReady(rive: Rive): boolean {
  return rive.stateMachineNames.length > 0 || rive.animationNames.length > 0;
}

/** Play the requested state machine, or the file's timeline when it has none. */
function playInteractive(rive: Rive, requested: string) {
  const names = rive.stateMachineNames;
  if (names.includes(requested)) {
    rive.play(requested);
    return;
  }
  const fallback = names[0];
  rive.stop();
  if (fallback) {
    rive.play(fallback);
    return;
  }
  rive.play();
}

/**
 * Renders a Rive (.riv) animation that fills its parent. Section and showcase tiles must
 * go through `ShowcaseTile` rather than using this directly.
 */
export function RiveTile({
  src,
  className,
  ariaLabel,
  stateMachines = null,
  active = true,
  interactive = false,
}: RiveTileProps) {
  const overlayFrozen = useTheaterOverlayFrozen();
  const shouldPlay = active && !overlayFrozen;
  // Named up front so Rive instances it during load. Detecting it afterwards never draws.
  const requestedMachine = stateMachines ?? (interactive ? DEFAULT_RIVE_STATE_MACHINE : null);

  const { rive, RiveComponent } = useRive({
    src,
    autoplay: true,
    ...(interactive ? { autoBind: true } : {}),
    ...(requestedMachine ? { stateMachines: requestedMachine } : {}),
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
  });

  useEffect(() => {
    if (!rive) return;
    if (!shouldPlay) {
      rive.pause();
      return;
    }
    if (!requestedMachine) {
      rive.play();
      return;
    }
    if (!interactive) {
      rive.play(requestedMachine);
      return;
    }
    if (isFileReady(rive)) {
      playInteractive(rive, requestedMachine);
      return;
    }
    const onLoad = () => playInteractive(rive, requestedMachine);
    rive.on(EventType.Load, onLoad);
    return () => rive.off(EventType.Load, onLoad);
  }, [rive, shouldPlay, requestedMachine, interactive]);

  return (
    <RiveComponent
      className={cn(className, interactive && "cursor-pointer")}
      {...(ariaLabel ? { role: "img", "aria-label": ariaLabel } : { "aria-hidden": true })}
    />
  );
}
